/**
 * OPPORTUNITY MOCK DATA
 *
 * Centralized mock data for opportunities including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT OPPORTUNITY LIST VIEWS
 */
export const defaultOpportunityListViews: ListView[] = [
  {
    id: 'lv_opps_all',
    name: 'All Opportunities',
    apiName: 'all_opportunities',
    objectApiName: 'Opportunity',
    description: 'All opportunity records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Opportunity ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 3 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'Probability__c', fieldLabel: 'Probability', fieldType: 'number', isVisible: true, order: 5 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 6 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 7 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_opps_open',
    name: 'Open Opportunities',
    apiName: 'open_opportunities',
    objectApiName: 'Opportunity',
    description: 'Active opportunities in pipeline',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'Probability__c', fieldLabel: 'Probability', fieldType: 'number', isVisible: true, order: 4 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 5 },
      { fieldApiName: 'NextStep__c', fieldLabel: 'Next Step', fieldType: 'text', isVisible: true, order: 6 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_opps_q1',
    name: 'Closing This Quarter',
    apiName: 'closing_this_quarter',
    objectApiName: 'Opportunity',
    description: 'Opportunities closing in Q1 2025',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'Stage__c', fieldLabel: 'Stage', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    sortBy: {
      fieldApiName: 'CloseDate__c',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_opps_won',
    name: 'Closed Won',
    apiName: 'closed_won_opportunities',
    objectApiName: 'Opportunity',
    description: 'Successfully closed opportunities',
    isDefault: false,
    columns: [
      { fieldApiName: 'Name__c', fieldLabel: 'Opportunity Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'ClientName__c', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'Amount__c', fieldLabel: 'Amount', fieldType: 'number', isVisible: true, order: 2 },
      { fieldApiName: 'CloseDate__c', fieldLabel: 'Close Date', fieldType: 'date', isVisible: true, order: 3 },
      { fieldApiName: 'Type__c', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'Owner__c', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'Stage__c', operator: 'equals', value: 'Closed Won' }
    ],
    sortBy: {
      fieldApiName: 'CloseDate__c',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT OPPORTUNITY RECORDS
 */
export const defaultOpportunityRecords: ListViewRecord[] = [
  {
    id: 'opp_001',
    Name__c: 'Enterprise Software License - Tech Innovations',
    ClientId__c: 'client_001',
    ClientName__c: 'Tech Innovations Inc',
    ContactId__c: 'contact_001',
    ContactName__c: 'Jennifer Taylor',
    Amount__c: 250000,
    ExpectedRevenue__c: 200000,
    Stage__c: 'Evaluation',
    Probability__c: 60,
    CloseDate__c: '2025-02-28',
    Owner__c: 'John Smith',
    Type__c: 'New Business',
    LeadSource__c: 'Website',
    NextStep__c: 'Send final contract for review',
    Description__c: 'Enterprise license deal for cloud platform. 3-year contract with annual renewals.',
    CreatedDate__c: '2024-12-15T09:00:00Z',
    CreatedBy__c: 'user_admin',
    LastModifiedDate__c: '2025-01-15T14:30:00Z',
    LastModifiedBy__c: 'user_john'
  },
  {
    id: 'opp_002',
    Name__c: 'Cloud Migration Services - Global Manufacturing',
    ClientId__c: 'client_002',
    ClientName__c: 'Global Manufacturing Corp',
    ContactId__c: 'contact_002',
    ContactName__c: 'Robert Chen',
    Amount__c: 500000,
    ExpectedRevenue__c: 300000,
    Stage__c: 'Pitch',
    Probability__c: 40,
    CloseDate__c: '2025-03-15',
    Owner__c: 'Sarah Johnson',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Deliver technical proposal presentation',
    Description__c: 'Full cloud migration including data transfer, training, and 12-month support.',
    CreatedDate__c: '2025-01-05T10:30:00Z',
    CreatedBy__c: 'user_sarah',
    LastModifiedDate__c: '2025-01-14T11:00:00Z',
    LastModifiedBy__c: 'user_sarah'
  },
  {
    id: 'opp_003',
    Name__c: 'POS System Upgrade - Retail Solutions',
    ClientId__c: 'client_003',
    ClientName__c: 'Retail Solutions LLC',
    Amount__c: 150000,
    Stage__c: 'Meeting Set',
    Probability__c: 10,
    CloseDate__c: '2025-04-30',
    Owner__c: 'Michael Chen',
    Type__c: 'Existing Customer - Upgrade',
    LeadSource__c: 'Trade Show',
    NextStep__c: 'Schedule discovery call',
    CreatedDate__c: '2025-01-10T14:00:00Z',
    LastModifiedDate__c: '2025-01-16T09:15:00Z'
  },
  {
    id: 'opp_004',
    Name__c: 'Financial Analytics Platform - FSG',
    ClientId__c: 'client_004',
    ClientName__c: 'Financial Services Group',
    Amount__c: 750000,
    Stage__c: 'Discovery',
    Probability__c: 20,
    CloseDate__c: '2025-03-31',
    Owner__c: 'Emily Davis',
    Type__c: 'New Business',
    LeadSource__c: 'Cold Call',
    NextStep__c: 'Complete needs assessment',
    CreatedDate__c: '2024-12-20T09:15:00Z',
    LastModifiedDate__c: '2025-01-13T16:45:00Z'
  },
  {
    id: 'opp_005',
    Name__c: 'Healthcare Data Security Solution',
    ClientId__c: 'client_005',
    ClientName__c: 'Healthcare Innovations',
    Amount__c: 350000,
    Stage__c: 'Closed Won',
    Probability__c: 100,
    CloseDate__c: '2025-01-15',
    Owner__c: 'David Wilson',
    Type__c: 'New Business',
    LeadSource__c: 'Partner',
    NextStep__c: 'Begin implementation',
    CreatedDate__c: '2024-11-10T11:30:00Z',
    LastModifiedDate__c: '2025-01-15T10:15:00Z'
  },
  {
    id: 'opp_006',
    Name__c: 'Project Management Software - Construction Pro',
    ClientId__c: 'client_006',
    ClientName__c: 'Construction Pro LLC',
    Amount__c: 85000,
    Stage__c: 'Closed Lost',
    Probability__c: 0,
    CloseDate__c: '2025-01-10',
    Owner__c: 'Sarah Johnson',
    Type__c: 'New Business',
    LeadSource__c: 'Website',
    NextStep__c: 'Archive opportunity',
    CreatedDate__c: '2024-11-05T13:00:00Z',
    LastModifiedDate__c: '2025-01-10T09:30:00Z'
  },
  {
    id: 'opp_007',
    Name__c: 'Learning Management System - EdTech',
    ClientId__c: 'client_007',
    ClientName__c: 'Education Tech Solutions',
    Amount__c: 200000,
    Stage__c: 'Evaluation',
    Probability__c: 60,
    CloseDate__c: '2025-02-15',
    Owner__c: 'John Smith',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Negotiate pricing and terms',
    CreatedDate__c: '2024-12-01T15:45:00Z',
    LastModifiedDate__c: '2025-01-14T11:20:00Z'
  },
  {
    id: 'opp_008',
    Name__c: 'Fleet Management System - Logistics',
    ClientId__c: 'client_008',
    ClientName__c: 'Logistics Central',
    Amount__c: 425000,
    Stage__c: 'Pitch',
    Probability__c: 40,
    CloseDate__c: '2025-03-20',
    Owner__c: 'Michael Chen',
    Type__c: 'Existing Customer - Replacement',
    LeadSource__c: 'Trade Show',
    NextStep__c: 'Submit detailed proposal',
    CreatedDate__c: '2024-12-28T08:30:00Z',
    LastModifiedDate__c: '2025-01-15T14:00:00Z'
  },
  {
    id: 'opp_009',
    Name__c: 'Marketing Automation Platform Expansion',
    ClientId__c: 'client_009',
    ClientName__c: 'Marketing Pros',
    Amount__c: 125000,
    Stage__c: 'Discovery',
    Probability__c: 20,
    CloseDate__c: '2025-04-15',
    Owner__c: 'Emily Davis',
    Type__c: 'Existing Customer - Upgrade',
    LeadSource__c: 'Website',
    NextStep__c: 'Demo advanced features',
    CreatedDate__c: '2025-01-08T09:00:00Z',
    LastModifiedDate__c: '2025-01-16T10:30:00Z'
  },
  {
    id: 'opp_010',
    Name__c: 'Enterprise DevOps Tools - Software Dynamics',
    ClientId__c: 'client_010',
    ClientName__c: 'Software Dynamics',
    Amount__c: 600000,
    Stage__c: 'Closed Won',
    Probability__c: 100,
    CloseDate__c: '2025-01-20',
    Owner__c: 'David Wilson',
    Type__c: 'New Business',
    LeadSource__c: 'Referral',
    NextStep__c: 'Kickoff meeting scheduled',
    CreatedDate__c: '2024-11-15T11:00:00Z',
    LastModifiedDate__c: '2025-01-20T10:00:00Z'
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedOpportunityListViews = createPersistedMockData(
  'crm_opportunity_list_views',
  defaultOpportunityListViews
);

export const persistedOpportunityRecords = createPersistedMockData(
  'crm_opportunity_records',
  defaultOpportunityRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockOpportunityListViews = defaultOpportunityListViews;
export const mockOpportunityRecords = defaultOpportunityRecords;
